import React from "react";
//#region redux
import { AppState } from "store";
import { useDispatch, useSelector } from "react-redux";
import {
  incomingInvoiceCancelled,
  incomingInvoiceConverted,
  incomingInvoiceSentBack,
  incomingInvoiceUpdated,
} from "store/actions/incomingInvoiceActions";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { ACTION_SEPARATOR, MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { IncomingInvoiceMoreActionProps } from "models/propTypes/incomingInvoicePropTypes";
import { StyleEnums, DialogEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { IncomingInvoiceFormTypes } from "models/formTypes";
//#endregion
//#region components
import IncomingInvoiceSendBackFormContainer from "./forms/sendBackFormContainer";
import useIncomingInvoiceDownload from "./useDownload";
import useIncomingInvoiceForwardAction from "./actions/useIncomingInvoiceForwardAction";
import useIncomingInvoiceSnoozeAction from "./actions/useIncomingInvoiceSnoozeAction";
import useUpdatePaymentDialog from "./useUpdatePaymentDialog";
import { MoreActions, useCheckRelations } from "components/common";
import { useIncomingInvoiceService } from "serviceHooks";
import useIncomingInvoicePermissions from "./usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const IncomingInvoiceMoreActions: React.FC<IncomingInvoiceMoreActionProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const dispatch = useDispatch();

  const forwardAction = useIncomingInvoiceForwardAction(props.invoice.id, props.reloadHandler);
  const snoozeAction = useIncomingInvoiceSnoozeAction(props.invoice.id, props.markAsDoneHandler);

  const loggedInUserId = useSelector((store: AppState) => store.auth.loggedInUser.personId);
  const {
    getIncomingInvoiceUserPermissions,
    getHasIncomingInvoiceSigneePermission,
    getHasIncomingInvoiceAdminPermission,
  } = useIncomingInvoicePermissions();
  const { getIsReadonlyUser } = useCheckRelations();

  //#region send back
  const onSendBackSuccess = () => {
    dispatch(incomingInvoiceSentBack(props.invoice.id));
    props.markAsDoneHandler();
  };

  const sendBackAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Return),
    icon: IconDefinitionConstants.WSendBack,
    actionDialog: {
      isOpen: false,
      title: `${LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Common.Return
      )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Invoice)}`,
      subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ReturnWhy),
      titleIcon: IconDefinitionConstants.WSendBack,
      dialogType: DialogEnums.DialogType.Content,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Return),
          successMessage: TranslationKeys.IncomingInvoice.SendBackSuccessMessage,
        },
      ],
      children: <IncomingInvoiceSendBackFormContainer invoiceId={props.invoice.id} />,
      maxWidth: "sm",
    },
    onActionSuccess: onSendBackSuccess,
  };
  //#endregion

  //#region update payment
  const onUpdatePaymentSuccess = (values: IncomingInvoiceFormTypes.UpdatePayment) => {
    props.onUpdatePaymentDialogSuccessHandler(values, (r) =>
      dispatch(incomingInvoiceUpdated(r, loggedInUserId, getIncomingInvoiceUserPermissions()))
    );
  };

  const updatePaymentDialog = useUpdatePaymentDialog();
  const updatePaymentAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EditPayment),
    icon: IconDefinitionConstants.WTransactions,
    actionDialog: updatePaymentDialog(
      {
        invoiceId: props.invoice.id,
        isAlreadyPaid: props.invoice.isAlreadyPaid,
        paymentDate: props.invoice.toPayDate,
      },
      onUpdatePaymentSuccess
    ),
  };
  //#endregion

  //#region convert invoice action
  const onClickNotAnInvoice = () => {
    return incomingInvoiceService.convertToGenericVoucher(props.invoice.id);
  };

  const onConvertInvoiceSuccess = () => {
    dispatch(incomingInvoiceConverted(props.invoice.id));
    props.markAsDoneHandler();
  };

  const convertInvoiceAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotAnInvoice),
    icon: IconDefinitionConstants.WQuestionCircle,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotAnInvoice),
      titleIcon: IconDefinitionConstants.WQuestionCircle,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Move),
          onClick: onClickNotAnInvoice,
          successMessage: TranslationKeys.IncomingInvoice.MovedToAccountingSuccessMessage,
        },
      ],
      dialogType: DialogEnums.DialogType.Message,
      children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotAnInvoiceMessage),
    },
    onActionSuccess: onConvertInvoiceSuccess,
  };
  //#endregion

  //#region download
  const onDownloadClick = useIncomingInvoiceDownload(props.invoice.id);

  const downloadAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Common.Download
    )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Invoice)}`,
    icon: IconDefinitionConstants.WDownload,
    onClickHandler: onDownloadClick,
  };
  //#endregion

  //#region cancel invoice
  const onClickCancel = () => {
    return incomingInvoiceService.cancel(props.invoice.id);
  };

  const onCancelSuccess = () => {
    dispatch(incomingInvoiceCancelled(props.invoice.id));
    props.markAsDoneHandler && props.markAsDoneHandler();
  };

  const cancelInvoiceAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
    icon: IconDefinitionConstants.WTrashAlt,
    actionDialog: {
      isOpen: false,
      title: `${LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.Common.Discard
      )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Invoice)}?`,
      titleIcon: IconDefinitionConstants.WTrashAlt,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Remove),
          onClick: onClickCancel,
          type: StyleEnums.DisplayType.Danger,
          successMessage: TranslationKeys.IncomingInvoice.CancelSuccessMessage,
        },
      ],
      dialogType: DialogEnums.DialogType.Message,
      children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DeleteWarningMessage),
      type: StyleEnums.DisplayType.Danger,
    },
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess: onCancelSuccess,
  };
  //#endregion

  const getMenuItems = (): MoreActionMenuItemType[] => {
    const actions: MoreActionMenuItemType[] = [];
    props.costForwardingAction && actions.push(props.costForwardingAction);
    const isReadonlyUser = getIsReadonlyUser();
    if (!isReadonlyUser) {
      const isReceived = props.invoice.state === ApiModel.IncomingInvoiceState.Received;
      const isToSign = props.invoice.state === ApiModel.IncomingInvoiceState.Signee;

      const isSignee = getHasIncomingInvoiceSigneePermission();

      if ((isToSign && isSignee) || isReceived) {
        const isDefault = !props.invoice.isAutoInvoice;

        actions.push(forwardAction);
        isReceived && actions.push(sendBackAction);
        actions.push(updatePaymentAction);
        isDefault && actions.push(convertInvoiceAction);
        actions.push(snoozeAction);
        actions.push(downloadAction);

        if (isDefault) {
          actions.push(ACTION_SEPARATOR);
          actions.push(cancelInvoiceAction);
        }
      } else {
        const isInvoiceAdmin = getHasIncomingInvoiceAdminPermission();
        if (isInvoiceAdmin && props.invoice.isBooked && !props.invoice.isPaid) actions.push(updatePaymentAction);
        actions.push(downloadAction);
      }
    } else {
      actions.push(downloadAction);
    }
    return actions;
  };

  return <MoreActions menuItems={getMenuItems()} disableSuccessMessages={props.disableSuccessMessages} />;
};

export default IncomingInvoiceMoreActions;
